import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import OfferBanner from "./OfferBanner";
import { TypeAnimation } from "react-type-animation";
import config from "../config";

const Header = ({ openModal }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  // Dropdowns
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [publicOpen, setPublicOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // ---- CLARITY helper ----
  const track = (name, props = {}) => {
    if (typeof window !== "undefined" && window.clarity) {
      window.clarity("event", name, props);
    }
  };

  const isActive = (path) => location.pathname === path;

  const closeAll = () => {
    setMenuOpen(false);
    setResourcesOpen(false);
    setPublicOpen(false);
    setAiOpen(false);
  };

  const goAuth = (type) => {
    const url = type === "login" ? config.signInUrl : config.signUpUrl;

    track("apply_cta_click", {
      placement: "header",
      target: type,
      mode: url?.startsWith("http") ? "external" : "internal",
    });

    if (url && url.startsWith("http")) {
      if (type === "login") {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        window.location.href = url;
      }
      return;
    }

    navigate(type === "login" ? "/login" : "/signup");
  };

  const handleFaqClick = () => {
    track("faq_click", { source: "header" });

    const scrollToFaq = () => {
      const faqSection = document.getElementById("faq");
      if (faqSection) faqSection.scrollIntoView({ behavior: "smooth" });
    };

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(scrollToFaq, 200);
    } else {
      scrollToFaq();
    }

    closeAll();
  };

  const activeClass = "text-orange-600 font-semibold";

  return (
    <header className="sticky top-0 z-50">
      <OfferBanner />

      <div className="bg-white/80 backdrop-blur-md shadow-md text-gray-800 px-6 py-4">
        <div className="flex justify-between items-center">
          {/* Logo & Tagline */}
          <div className="flex items-center gap-4">
            <img src="/ssrlogo3.png" alt="myBOQ Logo" className="w-12 h-auto" />
            <div className="flex flex-col">
              <div className="text-2xl font-bold leading-tight">
                <Link
                  to="/"
                  className="hover:text-orange-500 transition-colors"
                  onClick={() => track("nav_click", { item: "home_logo" })}
                >
                  myBOQ
                </Link>
              </div>

              <a
                href="https://siliconmount.com"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-0.5 inline-flex items-center gap-1 text-[11px] md:text-xs text-gray-500 hover:text-orange-600 hover:underline transition"
                aria-label="Powered by SiliconMount Tech Services (opens in new tab)"
                onClick={() =>
                  track("powered_by_click", { placement: "logo_block" })
                }
              >
                <span>Powered by</span>
                <span className="font-semibold">SiliconMount Tech Services</span>
              </a>

              <TypeAnimation
                sequence={[
                  "Estimate.",
                  1500,
                  "Automate.",
                  1500,
                  "Comply.",
                  1500,
                  "Estimate. Automate. Comply.",
                  2500,
                ]}
                speed={50}
                wrapper="span"
                repeat={Infinity}
                className="text-xs italic text-gray-600 tracking-wide"
              />
            </div>
          </div>

          {/* Hamburger (mobile) */}
          <div className="md:hidden">
            <button
              onClick={() => {
                const next = !menuOpen;
                setMenuOpen(next);
                track("mobile_menu_toggle", { open: next });
                if (!next) closeAll();
              }}
              className="focus:outline-none"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {menuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>

          {/* Desktop Nav (clean premium) */}
          <nav className="hidden md:flex items-center gap-7">
            <Link
              to="/"
              className={`font-semibold hover:text-orange-500 transition ${
                isActive("/") ? activeClass : ""
              }`}
              onClick={() => track("nav_click", { item: "home" })}
            >
              Home
            </Link>

            <Link
              to="/features"
              className={`font-semibold hover:text-orange-500 transition ${
                isActive("/features") ? activeClass : ""
              }`}
              onClick={() => track("nav_click", { item: "platform" })}
            >
              Platform
            </Link>

            {/* Public Sector */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setPublicOpen((v) => !v);
                  setAiOpen(false);
                  setResourcesOpen(false);
                  track("nav_click", { item: "public_sector_menu" });
                }}
                className={`font-semibold hover:text-orange-500 transition ${
                  location.pathname.startsWith("/public-sector")
                    ? activeClass
                    : ""
                }`}
              >
                Public Sector ▾
              </button>

              {publicOpen && (
                <div
                  className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-2xl shadow-lg p-2"
                  onMouseLeave={() => setPublicOpen(false)}
                >
                  <Link
                    to="/public-sector"
                    className="block px-3 py-2 rounded-xl hover:bg-orange-50"
                    onClick={() => {
                      setPublicOpen(false);
                      track("nav_click", { item: "public_sector_overview" });
                    }}
                  >
                    Overview
                  </Link>
                  <Link
                    to="/public-sector/use-cases"
                    className="block px-3 py-2 rounded-xl hover:bg-orange-50"
                    onClick={() => {
                      setPublicOpen(false);
                      track("nav_click", { item: "public_sector_use_cases" });
                    }}
                  >
                    Use Cases
                  </Link>
                  <Link
                    to="/public-sector/compliance"
                    className="block px-3 py-2 rounded-xl hover:bg-orange-50"
                    onClick={() => {
                      setPublicOpen(false);
                      track("nav_click", { item: "public_sector_compliance" });
                    }}
                  >
                    Compliance & Standardization
                  </Link>
                  <Link
                    to="/public-sector/institutions"
                    className="block px-3 py-2 rounded-xl hover:bg-orange-50"
                    onClick={() => {
                      setPublicOpen(false);
                      track("nav_click", { item: "public_sector_institutions" });
                    }}
                  >
                    For Institutions
                  </Link>
                </div>
              )}
            </div>

            {/* AI & Innovation */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setAiOpen((v) => !v);
                  setPublicOpen(false);
                  setResourcesOpen(false);
                  track("nav_click", { item: "ai_menu" });
                }}
                className={`font-semibold hover:text-orange-500 transition ${
                  location.pathname.startsWith("/ai") ? activeClass : ""
                }`}
              >
                AI & Innovation ▾
              </button>

              {aiOpen && (
                <div
                  className="absolute top-full left-0 mt-2 w-56 bg-white border border-gray-200 rounded-2xl shadow-lg p-2"
                  onMouseLeave={() => setAiOpen(false)}
                >
                  <Link
                    to="/ai"
                    className="block px-3 py-2 rounded-xl hover:bg-orange-50"
                    onClick={() => {
                      setAiOpen(false);
                      track("nav_click", { item: "ai_overview" });
                    }}
                  >
                    Overview
                  </Link>
                  <Link
                    to="/ai/roadmap"
                    className="block px-3 py-2 rounded-xl hover:bg-orange-50"
                    onClick={() => {
                      setAiOpen(false);
                      track("nav_click", { item: "ai_roadmap" });
                    }}
                  >
                    AI Roadmap
                  </Link>
                  <Link
                    to="/ai/pilot"
                    className="block px-3 py-2 rounded-xl hover:bg-orange-50"
                    onClick={() => {
                      setAiOpen(false);
                      track("nav_click", { item: "ai_pilot" });
                    }}
                  >
                    Pilot Program
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/pricing"
              className={`font-semibold hover:text-orange-500 transition ${
                isActive("/pricing") ? activeClass : ""
              }`}
              onClick={() => track("nav_click", { item: "pricing" })}
            >
              Pricing
            </Link>

            {/* Resources */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setResourcesOpen((v) => !v);
                  setPublicOpen(false);
                  setAiOpen(false);
                  track("nav_click", { item: "resources_menu" });
                }}
                className="font-semibold hover:text-orange-500 transition"
              >
                Resources ▾
              </button>

              {resourcesOpen && (
                <div
                  className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-2xl shadow-lg p-2"
                  onMouseLeave={() => setResourcesOpen(false)}
                >
                  <Link
                    to="/blog"
                    className="block px-3 py-2 rounded-xl hover:bg-orange-50"
                    onClick={() => {
                      setResourcesOpen(false);
                      track("nav_click", { item: "blog" });
                    }}
                  >
                    Blog
                  </Link>

                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 rounded-xl hover:bg-orange-50"
                    onClick={() => {
                      setResourcesOpen(false);
                      handleFaqClick();
                    }}
                  >
                    FAQs
                  </button>

                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 rounded-xl hover:bg-orange-50"
                    onClick={() => {
                      setResourcesOpen(false);
                      track("contact_click", { placement: "resources_dropdown" });
                      openModal();
                    }}
                  >
                    Contact
                  </button>

                  <a
                    href="/myBOQ_Information_Brochure_v2.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-3 py-2 rounded-xl hover:bg-orange-50"
                    onClick={() =>
                      track("brochure_download", {
                        placement: "resources_dropdown",
                        file: "myBOQ_Information_Brochure_v2.pdf",
                      })
                    }
                  >
                    Download Brochure
                  </a>
                </div>
              )}
            </div>

            {/* Right-side auth */}
            <button
              onClick={() => goAuth("login")}
              className="font-semibold text-gray-700 hover:text-orange-500 transition"
            >
              Login
            </button>

            <button
              onClick={() => goAuth("signup")}
              className="bg-orange-500 text-white font-semibold px-5 py-2 rounded-full hover:bg-orange-600 transition shadow-sm"
            >
              Get Started Free
            </button>
          </nav>
        </div>

        {/* Mobile Nav (clean sections) */}
        {menuOpen && (
          <div className="md:hidden mt-4 flex flex-col gap-4 items-center text-gray-800">
            <Link
              to="/"
              onClick={() => {
                closeAll();
                track("nav_click", { item: "home", source: "mobile" });
              }}
              className="hover:text-orange-500 transition"
            >
              Home
            </Link>

            <Link
              to="/features"
              onClick={() => {
                closeAll();
                track("nav_click", { item: "platform", source: "mobile" });
              }}
              className="hover:text-orange-500 transition"
            >
              Platform
            </Link>

            {/* Public Sector (expand) */}
            <button
              onClick={() => {
                setPublicOpen((v) => !v);
                setAiOpen(false);
                setResourcesOpen(false);
              }}
              className="font-semibold hover:text-orange-500 transition"
            >
              Public Sector {publicOpen ? "▴" : "▾"}
            </button>
            {publicOpen && (
              <div className="flex flex-col gap-2 items-center text-sm">
                <Link
                  to="/public-sector"
                  onClick={() => closeAll()}
                  className="hover:text-orange-500"
                >
                  Overview
                </Link>
                <Link
                  to="/public-sector/use-cases"
                  onClick={() => closeAll()}
                  className="hover:text-orange-500"
                >
                  Use Cases
                </Link>
                <Link
                  to="/public-sector/compliance"
                  onClick={() => closeAll()}
                  className="hover:text-orange-500"
                >
                  Compliance
                </Link>
                <Link
                  to="/public-sector/institutions"
                  onClick={() => closeAll()}
                  className="hover:text-orange-500"
                >
                  Institutions
                </Link>
              </div>
            )}

            {/* AI (expand) */}
            <button
              onClick={() => {
                setAiOpen((v) => !v);
                setPublicOpen(false);
                setResourcesOpen(false);
              }}
              className="font-semibold hover:text-orange-500 transition"
            >
              AI & Innovation {aiOpen ? "▴" : "▾"}
            </button>
            {aiOpen && (
              <div className="flex flex-col gap-2 items-center text-sm">
                <Link to="/ai" onClick={() => closeAll()} className="hover:text-orange-500">
                  Overview
                </Link>
                <Link
                  to="/ai/roadmap"
                  onClick={() => closeAll()}
                  className="hover:text-orange-500"
                >
                  Roadmap
                </Link>
                <Link
                  to="/ai/pilot"
                  onClick={() => closeAll()}
                  className="hover:text-orange-500"
                >
                  Pilot
                </Link>
              </div>
            )}

            <Link
              to="/pricing"
              onClick={() => closeAll()}
              className="hover:text-orange-500 transition"
            >
              Pricing
            </Link>

            <Link
              to="/blog"
              onClick={() => closeAll()}
              className="hover:text-orange-500 transition"
            >
              Blog
            </Link>

            <button
              onClick={() => {
                handleFaqClick();
                closeAll();
              }}
              className="hover:text-orange-500 transition"
            >
              FAQs
            </button>

            <button
              onClick={() => {
                track("contact_click", { placement: "mobile_header" });
                openModal();
                closeAll();
              }}
              className="hover:text-orange-500 transition"
            >
              Contact
            </button>

            <button
              onClick={() => {
                closeAll();
                goAuth("login");
              }}
              className="w-3/4 text-center border border-gray-200 text-gray-800 font-semibold px-4 py-2 rounded-full hover:bg-gray-50 transition"
            >
              Login
            </button>

            <button
              onClick={() => {
                closeAll();
                goAuth("signup");
              }}
              className="w-3/4 text-center bg-orange-500 text-white font-semibold px-4 py-2 rounded-full hover:bg-orange-600 transition shadow-sm"
            >
              Get Started Free
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;